'use client';

import { useEffect, useRef, useState } from 'react';

/**
 * Hook to detect when an element comes into view and add 'in-view' class
 * Works with the global .animate-in-view CSS class
 */
export function useInView<T extends HTMLElement = HTMLElement>(options: IntersectionObserverInit = {}) {
  const [isInView, setIsInView] = useState(false);
  const ref = useRef<T>(null);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    // Check for reduced motion preference
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    
    // If reduced motion is enabled, immediately show the element without animation
    if (prefersReducedMotion) {
      setIsInView(true);
      element.classList.add('in-view');
      return;
    }

    const observerOptions = {
      threshold: 0.25,
      rootMargin: '0px 0px -125px 0px', // Trigger when element is 125px from bottom of viewport
      ...options, // Allow override of default values
    };

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          element.classList.add('in-view');
          // Once in view, we can optionally disconnect
          // observer.disconnect();
        }
      },
      observerOptions
    );

    observer.observe(element);

    // Check initial intersection state in case element is already in view
    // Use a small delay to ensure layout is complete
    const checkInitialState = () => {
      const rect = element.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const isInViewport = rect.top < viewportHeight - 125 && rect.bottom > 0;
      
      if (isInViewport && rect.top < viewportHeight * 0.75) {
        // Element is already in view, trigger animation
        setIsInView(true);
        element.classList.add('in-view');
      }
    };

    // Check after a brief delay to ensure layout is stable
    const timeoutId = setTimeout(checkInitialState, 100);

    return () => {
      clearTimeout(timeoutId);
      observer.disconnect();
    };
  }, [options]);

  return { ref, isInView };
}

/**
 * Hook to detect when a container comes into view and add 'in-view' class
 * Works with the global .animate-stagger-in-view CSS class
 * Children should have the .stagger-item class to animate in sequence
 */
export function useStaggeredInView<T extends HTMLElement = HTMLElement>(options: IntersectionObserverInit = {}) {
  const [isInView, setIsInView] = useState(false);
  const ref = useRef<T>(null);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    // Check for reduced motion preference
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    
    // If reduced motion is enabled, immediately show the element without animation
    if (prefersReducedMotion) {
      setIsInView(true);
      element.classList.add('in-view');
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          element.classList.add('in-view');
          // Once in view, we can optionally disconnect
          // observer.disconnect();
        }
      },
      {
        threshold: 0.25,
        rootMargin: '0px 0px -125px 0px', // Trigger when element is 125px from bottom of viewport
        ...options, // Allow override of default values
      }
    );

    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, [options]);

  return { ref, isInView };
}

